import _get from 'lodash/get';
import _toString from 'lodash/toString';
import _each from 'lodash/each';
import Device from '../../utils/device';
import { MediaPlayerEvents } from 'mlb-media-player';
import BaseAdvertisingPlugin from '../base-advertising-plugin';
import AdvertisingPluginEvents from '../advertising-plugin-events';
import ScriptLoader from '../../utils/script-loader';

/**
 *
 * @module FreewheelPlugin
 * @desc
 * @class FreewheelPlugin
 *
 */

class FreewheelPlugin extends BaseAdvertisingPlugin {

    constructor(config, obsvervationCenter) {

        super(config, obsvervationCenter);

        this.pluginSubject = this.observationCenter.pluginSubject;

        this.videoAdContainerId = _get(this.config, 'videoAdContainerId', 'ad-container');

        this.adContainer = undefined;

        this.controls = _get(this.config, 'controls', false);

        this.autoplay = _get(this.config, 'autoplay', true);

        this.muted = _get(this.config, 'muted', true);

        this.playsInline = _get(this.config, 'playsinline', true);

        this.scriptUrl = _get(this.config, 'freewheel.adManager', 'https://mssl.fwmrm.net/p/release/latest-JS/adm/prd/AdManager.js');

        this.context = null;

        this.siteSection = _get(this.config, 'freewheel.siteSection');

        this.networkId = _get(this.config, 'freewheel.siteSectionNetwork');

        this.fallbackNetwork = _get(this.config, 'freewheel.fallbackNetwork');

        this.topicId = _get(this.config, 'freewheel.topicId');

        this.prerollSlots = [];

        this.midrollSlots = [];

        this.overlaySlots = [];

        this.postrollSlots = [];

        this.sourceUrlQueue = [];

        this.currentPlayingSlotType = null;

        this.deviceManager = new Device();

        this.videoAdNode = undefined;

        /**
         *
         * @type {boolean}
         * @desc This flag is used by admins to end midrolls early, flag is change to true in endMidRoll()
         *
         */
        this.endMidRolls = false;

        this.initAdContainer();

    }

    createVideoAdPlaybackNode() {

        const videoAdNode = document.createElement('video');

        videoAdNode.setAttribute('id', 'paid-interruption-player');

        videoAdNode.controls = this.controls;
        videoAdNode.autoplay = this.autoplay;
        videoAdNode.muted = this.muted;
        videoAdNode.playsinline = this.playsInline;

        return videoAdNode;

    }

    /**
     *
     * @desc Initialize and create the ad container and video node. Also attaches the video node to the DOM
     * as a child of the ad container. The video node is prepended to the children of the adContainer
     *
     */
    initAdContainer() {

        this.adContainer = document.getElementById(this.videoAdContainerId);

        if (!this.adContainer) {

            throw new Error(`An element with the id ${this.videoAdContainerId} is required for ad playback`);

        }

        this.videoAdNode = this.createVideoAdPlaybackNode();

        this.adContainer.style.display = 'none';
        this.adContainer.innerHTML = ''; // Clear all the children
        this.adContainer.appendChild(this.videoAdNode); // Add the video node

    }

    /**
     *
     * @desc Subscribe to the ObservationCenter messages
     *
     */
    subscribeToMessages() {

        const PlayerEvents = MediaPlayerEvents[this.mediaPlayer.player.playerTypeConfig.playerType];

        // Player subscriptions
        const playerSubscription = this.observationCenter.playerSubject.subscribe((data) => {

            switch(data.type) {

                case PlayerEvents.VOLUME_CHANGE: {

                    const volume = _get(data, 'event.target.volume');
                    const muted = _get(data, 'event.target.muted');

                    this.onVolumeChanged(volume, muted);
                    break;

                }

                case PlayerEvents.MEDIA_COMPLETE: {

                    this.onContentVideoEnded();

                    break;

                }
                case PlayerEvents.TIME_UPDATE: {

                    this.onContentVideoTimeUpdated();

                    break;

                }


            }

        });

        // Plugin subscriptions

        const pluginSubscription = this.observationCenter.pluginSubject.subscribe((data) => {

            switch(data.type) {

                case AdvertisingPluginEvents.AD_START: {

                    this.mediaPlayer.setIsAdPlaying(true);
                    this.adContainer.style.display = '';
                    break;

                }

                case AdvertisingPluginEvents.AD_COMPLETE: {

                    this.mediaPlayer.setIsAdPlaying(false);
                    this.adContainer.style.display = 'none';
                    break;

                }

                case AdvertisingPluginEvents.AD_ERROR:
                case AdvertisingPluginEvents.AD_PLUGIN_ERROR:
                case AdvertisingPluginEvents.AD_BLOCK_DETECTED: {

                    this.pluginDidError = true;
                    this.mediaPlayer.setIsAdPlaying(false);
                    this.destroy();
                    break;

                }

            }

        });

        this.subscriptions.push(playerSubscription);
        this.subscriptions.push(pluginSubscription);

    }

    /**
     *
     * @desc Unsubscribe from the event stream
     * @private
     *
     */
    unsubscribe() {

        this.subscriptions.forEach((subscription) => {

            subscription.unsubscribe();

        });

    }

    setMediaPlayer(mediaPlayer) {

        /* If we have a mediaPlayer, we need to unsubscribe from the event streams
            because we're going to resubscribe shorlty for the new player */
        if (this.mediaPlayer) {

            this.unsubscribe();

        }

        // Set so subscribeToMessages has a reference
        this.mediaPlayer = mediaPlayer;

        // init the freewheel plugin
        return this.init().then(() => {

            this.subscribeToMessages();
            // Let the super know (this also calls pluginReady())
            super.setMediaPlayer(mediaPlayer);

        });

    }

    /**
     *
     * @desc Initialize the plugin.
     *
     */
    init() {

        return Promise
            .resolve(_get(window, 'tv.freewheel.SDK'))
            .then((currentFreewheelSDK) => {

                if(!currentFreewheelSDK) {

                    return ScriptLoader.getAPI(this.scriptUrl, 'tv.freewheel.SDK');

                }
                // console.log('load ad manager');
                return currentFreewheelSDK;

            })
            .then((freewheelSDK) => {

                // console.log('ad manager loaded');
                if(!this.freewheelSDK) {

                    this.freewheelSDK = freewheelSDK;

                }
                this._onScriptLoaded();

            })
            .catch((error) => {

                console.log('ad manager error', error);
                // Dispatch error message
                this.pluginSubject.next({
                    type: AdvertisingPluginEvents.AD_PLUGIN_ERROR,
                    error,
                });

            });

    }

    /**
     *
     * @desc Request an Ad from the Freewheel server for playback.
     * @param {object} metadata metadata used to set Freewheel request and context properties
     *
     */
    requestAd(metadata) {

        const playerElement = this.videoAdNode;

        this.setClip(metadata);

        super.requestAd(playerElement);

        if (this.ready) {

            if (!this.adManager) {

                this.shouldPlayPreroll = false;

                console.error('NO AD MANAGER. Ad blocker enabled?');

                // Send AD_ERROR message
                this.pluginSubject.next({
                    type: AdvertisingPluginEvents.AD_BLOCK_DETECTED,
                    result,
                });

                return;

            }

            var duration = _get(this, 'config.freewheel.duration', 600);

            this.context = this.adManager.newContext();

            this.context.setProfile(this.config.freewheel.adProfile);
            this.context.setSiteSection(
                this.siteSection,
                this.networkId,
                Math.floor(Math.random() * 10000),
                this.freewheelSDK.ID_TYPE_CUSTOM, this.fallbackNetwork
            );

            this.context.setVideoAsset(_get(this.clip, 'content_id', 'MP4'), duration, this.networkId);

            if (typeof this.topicId !== 'undefined') {

                var site = this.topicId.substr(0, this.topicId.indexOf('/'));
                var player = this.topicId.slice(this.topicId.lastIndexOf('/') + 1, this.topicId.length);
                var section = this.topicId.slice(this.topicId.indexOf('/') + 1, this.topicId.lastIndexOf('/'));

                this.context.addKeyValue('topicID', this.topicId);
                this.context.addKeyValue('player', player);
                this.context.addKeyValue('site', site);
                this.context.addKeyValue('section', section);

            } else {

                this.context.addKeyValue('player', 'embed');
                this.context.addKeyValue('site', this.config.freewheel.site);

            }

            this.context.addKeyValue('deviceType', this.deviceManager.formFactor.toLowerCase());
            this.addCustomFreewheelProperties();

            /**
             *
             * 1.x has this (this.context.setContentVideoElement) method as this.context.registerVideoDisplayBase.
             * It appears that the different is the current method takes a video element
             * and the previous one takes a div id.
             *
             */
            //this.context.registerVideoDisplayBase('media-player');
            this.context.setContentVideoElement(playerElement);

            this.context.addTemporalSlot('Preroll_1', this.freewheelSDK.ADUNIT_PREROLL, 0);

            // Submit request.
            try {

                // Listen to AdManager Events
                this.context.addEventListener(
                    this.freewheelSDK.EVENT_REQUEST_COMPLETE,
                    (event) => {

                        this._onRequestComplete(event);

                    }
                );
                this.context.addEventListener(this.freewheelSDK.EVENT_AD, (event) => {

                    this._onAdEvent(event);

                });

                this.pluginSubject.next({ type: AdvertisingPluginEvents.AD_REQUEST_STARTED });

                this.context.submitRequest();

            } catch (e) {

                // Send AD_ERROR message
                console.log('requestAd error: ', e);
                this.pluginSubject.next({
                    type:AdvertisingPluginEvents.AD_ERROR,
                    e,
                });

            }

        } else {

            //if FreewheelPlugin is ready and this is getting triggered when it doesn't have a clip object,
            //this wreaks havoc on the player
            if (this.ready) {

                // Send AD_COMPLETE message
                this.pluginSubject.next({
                    type:AdvertisingPluginEvents.AD_COMPLETE,
                    player: this.player,
                });
                return;

            } else {

                var self = this;
                window.setTimeout(function() {

                    self.requestAd(playerElement);

                }, 500);

            }

        }

    };

    /**
     *
     * @desc Copy properties from clip.freewheelProps into the clip
     * @private
     *
     */
    addCustomFreewheelProperties() {

        try {

            //Reading values from ClipReader is preferred for the decoupling from knowing about the clip
            var clipReader = this.player.utils.getClipReader();
            var freewheelProps = clipReader.getFreewheelProps(this.clip);

        } catch (e) {

            //Couldn't use ClipReader. Manually look for the properties. This is not preferred as the keys on the clip might change, but here for legacy support
            var freewheelProps = _get(this.clip, 'freewheelProps');

        }

        _each(freewheelProps, function(value, key) {

            this.context.addKeyValue(key, _toString(value));

        }.bind(this));

    }

    /**
     *
     * @desc Called to begin playback of the pre-roll ad.
     * @private
     *
     */
    _actualPlayback() {

        if (this.prerollSlots.length) {

            this.prerollSlots[0].play();

        } else {

            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_COMPLETE,
                player: this.player,
            });

        }

    }

    /**
     *
     * @desc Called when the video's ended event fires.
     *
     */
    onContentVideoEnded() {

        if (this.freewheelSDK && (this.currentPlayingSlotType === this.freewheelSDK.TIME_POSITION_CLASS_MIDROLL)) {

            console.log('Received content video ended event dispatched from midroll, ignore');
            return;

        }

        this.sourceUrlQueue.shift();

        this.context.setVideoState(this.freewheelSDK.VIDEO_STATE_STOPPED);

        if (this.midrollSlots.length) {

            this.currentPlayingSlotType = this.freewheelSDK.TIME_POSITION_CLASS_MIDROLL;
            this.midrollSlots[0].play();

        } else if (this.sourceUrlQueue.length) {

            this._actualPlayback.call(this);

        } else if (this.postrollSlots && this.postrollSlots.length) {

            this.postrollSlots[0].play();

        } else {

            this.context.setVideoState(this.freewheelSDK.VIDEO_STATE_COMPLETED);

        }

    }

    /**
     *
     * @desc Called when the video's event timeupdate event fires.
     *
     */
    onContentVideoTimeUpdated() {

        if (this.freewheelSDK && (this.currentPlayingSlotType === this.freewheelSDK.TIME_POSITION_CLASS_MIDROLL)) {

            //TODO fire data
            console.log('Received content video timeUpdate event dispatched when midroll playing, ignore');
            return;

        }

        for (var i = 0; i < this.overlaySlots.length; i++) {

            let slot = this.overlaySlots[i];
            let slotTimePosition = slot.getTimePosition();
            let videoCurrentTime = this.player.currentTime;

            if (videoCurrentTime - slotTimePosition >= 0
                && videoCurrentTime - slotTimePosition <= 1
                && !this.currentPlayingSlotType) {

                this.overlaySlots.splice(i, 1);
                // console.log('onContentVideoTimeUpdated timePositionClass: ' + slot.getTimePositionClass());
                slot.play();

                return;

            }

        }

    }

    /**
     *
     * @desc Triggers on HTMLMediaElement timeupdate event.
     * @param {Object} e The timeupdate event data.
     *
     */
    onTimeUpdated(e) {

        var payload = { currentTime: e.target.currentTime };

        // Send AD_TIME_UPDATE_ELAPSED message
        this.pluginSubject.next({
            type: AdvertisingPluginEvents.AD_TIME_UPDATE_ELAPSED,
            payload,
        });

    }

    onVolumeChanged(volume, muted) {

        const playerElement = this.videoAdNode;

        if(playerElement) {

            playerElement.muted = muted;
            playerElement.volume = volume;

        }

    }

    /**
     *
     * @desc Fires when the Freewheel library script has loaded.
     *
     */
    _onScriptLoaded() {

        this.adManager = new this.freewheelSDK.AdManager();
        this.adManager.setNetwork(this.config.freewheel.adNetwork);
        this.adManager.setServer(this.config.freewheel.adServer);

        this.ready = true;

        // Send AD_PLUGIN_LOADED message
        this.pluginSubject.next({ type: AdvertisingPluginEvents.AD_PLUGIN_LOADED });

    }

    /**
     *
     * @private
     *
     */
    _playMidRoll() {

        if (this.midrollSlots.length) {

            this.midrollSlots[0].play();

        } else {

            // Send AD_COMPLETE message
            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_COMPLETE,
                player: this.player,
            });

        }

    }

    /**
     *
     * @desc Updates the event streams with ad playback info. This updates both `AD_TIME_UPDATE_REMAINING` and
     * `AD_TIME_UPDATE_ELAPSED` for backwards compatibility with the previous implementation.
     * @param {number} duration the entire duration of the ad break
     * @param {number} currentTime the current time of the current ad playback
     *
     */
    updateAdPlaybackInfo(duration, currentTime) {

        this.pluginSubject.next({
            type: AdvertisingPluginEvents.AD_TIME_UPDATE_REMAINING,
            countdown: duration - currentTime,
            duration,
            currentTime,
        });

        this.pluginSubject.next({
            type: AdvertisingPluginEvents.AD_TIME_UPDATE_ELAPSED,
            countdown: duration - currentTime,
            duration,
            currentTime,
        });

    }

    /**
     *
     * @desc Monitors ad playback for duration and current time via an interval and is
     * started on slot start and stopped on slot end or error. Updates the plugins subject for
     * `AD_TIME_UPDATE_REMAINING` and `AD_TIME_UPDATE_ELAPSED`
     * @param {object} slot the current ad slot
     *
     */
    startSlotCountdown(slot) {

        slot.countdownInterval = setInterval(() => {

            let duration = 0;
            const currentTime = slot.getPlayheadTime();

            this.prerollSlots.forEach((slot) => {

                duration += slot.getTotalDuration();

            });

            this.updateAdPlaybackInfo(duration, currentTime);

        }, 1000);

    }

    /**
     *
     * @desc Clears the countdown monitor interval, and updates countdown info to zero for duration
     * and currentTime.
     * @param {object} slot the slot to end countdown monitoring for
     *
     */
    endSlotCountdown(slot) {

        clearInterval(slot.countdownInterval);
        this.updateAdPlaybackInfo(0, 0);

    }

    /**
     *
     *
     * @esc Called when an ad slot has started to play.
     * @param {Object} e The event data for the request.
     *
     */
    _onSlotStarted(e) {

        const slot = e.slot;
        const slotTimePositionClass = slot.getTimePositionClass();
        // console.log('FreeWheelPlugin onSlotStarted slotTimePositionClass: ' + slotTimePositionClass);

        if (this.prerollSlots.length) {

            this.startSlotCountdown(this.prerollSlots[0]);

        }

    }

    /**
     *
     * @desc Called when an ad slot has started to play.
     * @param {Object} e The event data for the request.
     *
     */
    _onAdEvent(e) {


        if (e.subType === this.freewheelSDK.EVENT_AD_IMPRESSION) {

            let totalDuration = 0;

            this.prerollSlots.forEach((slot) => {

                totalDuration += slot.getTotalDuration();

            });

            // console.log('AD START! Duration', totalDuration);

            // Send AD_START message
            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_START,
                duration: totalDuration,
                adDuration: e.slot.getTotalDuration(),
                player: this.player,
            });

        }

        if (e.subType === this.freewheelSDK.EVENT_ERROR) {

            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_ERROR,
                error: e,
            });

            this.endSlotCountdown(e.slot);

        }

        if (e.adInstance.getEventCallbackUrls(tv.freewheel.SDK.EVENT_AD_CLICK, tv.freewheel.SDK.EVENT_TYPE_CLICK).length > 0) {

            var slot = e.target._temporalSlotBase;

            if (slot && slot.parentElement) {

                // console.log('FreewheelPlugin: add the class `freewheel_adClickthru`');

                slot.parentElement.classList.add('freewheel_adClickthru');

            }

        }

        if (e.subType === this.freewheelSDK.EVENT_AD_AUTO_PLAY_BLOCKED) {

            // Send AD_AUTOPLAY_BLOCKED message
            this.pluginSubject.next({ type: AdvertisingPluginEvents.AD_AUTOPLAY_BLOCKED });

        }

    }

    /**
     *
     * @desc Called when an ad slot has ended.
     * @param {Object} e The event data for the request.
     *
     */
    _onSlotEnded(e) {

        const slot = e.slot;
        var slotTimePositionClass = slot.getTimePositionClass();
        // console.log('FreeWheelPlugin onSlotEnded slotTimePositionClass: ' + slotTimePositionClass);

        this.endSlotCountdown(slot);

        var data;
        switch (slotTimePositionClass) {

            case this.freewheelSDK.TIME_POSITION_CLASS_PREROLL:
                if (this.prerollSlots.length) {

                    this.prerollSlots.shift();
                    if (this.prerollSlots.length) {

                        this.prerollSlots[0].play();

                    } else {

                        this.context.setVideoState(this.freewheelSDK.VIDEO_STATE_PLAYING);

                        data = { duration: e.slot ? e.slot.getTotalDuration() : null };

                        // Send AD_COMPLETE message
                        this.pluginSubject.next({
                            type: AdvertisingPluginEvents.AD_COMPLETE,
                            player: this.player,
                            data: data,
                        });


                    }

                }
                break;
            case this.freewheelSDK.TIME_POSITION_CLASS_MIDROLL:
                if (this.midrollSlots.length) {

                    this.midrollSlots.shift();
                    if (this.midrollSlots.length && !this.endMidRolls) {

                        this.midrollSlots[0].play();

                    } else {

                        this.currentPlayingSlotType = null;
                        this.endMidRolls = false;

                        this.context.setVideoState(this.freewheelSDK.VIDEO_STATE_PLAYING);

                        data = { duration: e.slot ? e.slot.getTotalDuration() : null };

                        // Send AD_COMPLETE message
                        this.pluginSubject.next({
                            type: AdvertisingPluginEvents.AD_COMPLETE,
                            player: this.player,
                            data: data,
                        });

                    }

                }
                break;
            case this.freewheelSDK.TIME_POSITION_CLASS_OVERLAY:
                this.currentPlayingSlotType = null;
                break;
            case this.freewheelSDK.TIME_POSITION_CLASS_POSTROLL:
                if (this.postrollSlots.length) {

                    this.postrollSlots.shift();
                    if (this.postrollSlots.length) {

                        this.postrollSlots[0].play();

                    } else {

                        this.context.setVideoState(this.freewheelSDK.VIDEO_STATE_COMPLETED);
                        _onRequestComplete.call(this, { // eslint-disable-line no-use-before-define
                            success: true,
                        });

                    }

                }
                break;

        }

        var fwClickThru = document.getElementsByClassName('freewheel_adClickthru');
        if (fwClickThru.length > 0) {

            // console.log('FreewheelPlugin: remove class `freewheel_adClickthru`');

            fwClickThru[0].classList.remove('freewheel_adClickthru');

        }

    }

    /**
     *
     * @desc Called when the FW Ad Manager receives a response from the FW system.
     * @param {Object} e The event data for the request.
     *
     */
    _onRequestComplete(result) {

        console.log('FreeWheelPlugin - _onRequestComplete', result);

        this.context.removeEventListener(this.freewheelSDK.EVENT_REQUEST_COMPLETE, function() {
        });

        if (!result.success) {

            // Send AD_ERROR message
            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_ERROR,
                result,
            });

            return;

        }

        this.prerollSlots = [];
        this.midrollSlots = [];
        this.overlaySlots = [];
        this.postrollSlots = [];

        var fwTemporalSlots = this.context.getTemporalSlots();
        var slot = null;

        for (var i = 0; i < fwTemporalSlots.length; i++) {

            slot = fwTemporalSlots[i];
            switch (slot.getTimePositionClass()) {

                case this.freewheelSDK.TIME_POSITION_CLASS_PREROLL:
                    this.prerollSlots.push(slot);
                    break;
                case this.freewheelSDK.TIME_POSITION_CLASS_MIDROLL:
                    this.midrollSlots.push(slot);
                    break;
                case this.freewheelSDK.TIME_POSITION_CLASS_OVERLAY:
                    this.overlaySlots.push(slot);
                    break;
                case this.freewheelSDK.TIME_POSITION_CLASS_POSTROLL:
                    this.postrollSlots.push(slot);
                    break;

            }

        }

        this.context.addEventListener(this.freewheelSDK.EVENT_SLOT_STARTED, (event) => {

            this._onSlotStarted(event);

        });
        this.context.addEventListener(this.freewheelSDK.EVENT_SLOT_ENDED, (event) => {

            this._onSlotEnded(event);

        });

        this.sourceUrlQueue = [this.clip];

        var NO_AD_SLOT = 'noAdSlot';
        var timePosition = (slot) ? slot.getTimePositionClass() : NO_AD_SLOT;

        switch (timePosition) {

            case NO_AD_SLOT:
            case this.freewheelSDK.TIME_POSITION_CLASS_PREROLL:
                this._actualPlayback();
                break;
            case this.freewheelSDK.TIME_POSITION_CLASS_MIDROLL:
                this._playMidRoll();
                break;
            case this.freewheelSDK.TIME_POSITION_CLASS_OVERLAY:
            case this.freewheelSDK.TIME_POSITION_CLASS_POSTROLL:
                break;

        }

        this.pluginSubject.next({
            type: AdvertisingPluginEvents.AD_REQUEST_COMPLETE,
            slots: fwTemporalSlots,
        });

    }


    // =================================================
    // PUBLIC (from previous implementation)
    // =================================================

    /**
     *
     * @desc
     * @param clip
     *
     */
    setClip(clip) {

        this.clip = clip;

    };


    startMidRoll(playerElement, adContainer, cuePointVO) {

        if (this.ready && playerElement) {

            if (!cuePointVO) {

                cuePointVO = new CuePointVO();

            }
            var timePosition = cuePointVO.timePosition;
            var breakLength = cuePointVO.breakLength;
            var midRollDuration = cuePointVO.duration;
            var networkId = this.networkId;

            if (this.clip) {

                videoAssetCustomId = this.clip.content_id || videoAssetCustomId;

            }

            this.context = this.adManager.newContext();

            this.context.setProfile(this.config.freewheel.adProfile);
            //Must set the request mode to REQUEST_MODE_LIVE for midRolls
            this.context.setRequestMode(this.freewheelSDK.REQUEST_MODE_LIVE);
            this.context.setSiteSection(
                this.siteSection,
                networkId,
                Math.floor(Math.random() * 10000),
                this.freewheelSDK.ID_TYPE_CUSTOM,
                this.fallbackNetwork
            );

            this.context.setVideoAsset(_get(this.clip, 'content_id', 'MP4'), midRollDuration, networkId);

            //this.context.registerVideoDisplayBase('media-player');
            this.context.setContentVideoElement(playerElement);

            this.context.addTemporalSlot(
                'Midroll_1',
                this.freewheelSDK.ADUNIT_MIDROLL,
                timePosition,
                null,
                1,
                breakLength,
                breakLength,
                null
            );

            // Submit request
            try {

                // Listen to AdManager Events
                this.context.addEventListener(
                    this.freewheelSDK.EVENT_REQUEST_COMPLETE,
                    (event) => {

                        this._onRequestComplete(event);

                    }
                );
                this.context.addEventListener(this.freewheelSDK.EVENT_AD, (event) => {

                    this._onAdEvent(event);

                });

                this.context.submitRequest();

            } catch (error) {

                // Send AD_ERROR message
                this.pluginSubject.next({
                    type: AdvertisingPluginEvents.AD_ERROR,
                    error,
                });

            }

        } else {

            var self = this;
            window.setTimeout(function() {

                self.startMidRoll(playerElement, cuePointVO);

            }, 500);

        }

    };

    endMidRoll() {

        var slot = this.midrollSlots[0];
        if (slot) {

            //Must set to true otherwise additional midrolls will run after stopping the current playing midroll
            this.endMidRolls = true;
            slot.stop();

        }

    };

    endPreRoll() {

        var slot = this.prerollSlots[0];
        if (slot) {

            //Must set to true otherwise additional midrolls will run after stopping the current playing midroll
            this.endPreRolls = true;
            slot.stop();

        }

    };

    /**
     *
     * @desc Resume a paused preroll if it was stopped because of EVENT_AD_AUTO_PLAY_BLOCKED
     * https://hub.freewheel.tv/display/techdocs/Safari+11+Auto-play+Video+Blocking+and+Solution
     *
     */
    resumeAd() {

        try {

            var currentSlot = this.prerollSlots[0];
            currentSlot.play();

            // Send AD_START message
            this.pluginSubject.next({
                type: AdvertisingPluginEvents.AD_START,
                duration: currentSlot.getTotalDuration(),
            });

        } catch (error) {

            // TODO: Should we send a message for resume ad failed?
            console.log('resumeAd failed. ' + error.message);

        }

    };

    /**
     *
     * @desc Destroy the ad manager.
     *
     */
    destroy() {


        if (this.context) {

            this.context.dispose();

        }

        super.destroy();

    }

    /**
     *
     * @desc Utility function for returning the name of the class.
     * @return string The class type.
     *
     */
    toString() {

        return 'FreewheelPlugin';

    }

}

export default FreewheelPlugin;
